import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';
import { Plus, Trash2, ExternalLink, Image } from 'lucide-react';

export const AdsTab = () => {
  const [banners, setBanners] = useState([]);
  const [adsenseConfig, setAdsenseConfig] = useState({ enabled: false, publisher_id: '', slots: {} });
  const [sponsoredConfig, setSponsoredConfig] = useState({ enabled: true, fees: { UK: 15, IN: 499, CA: 20 }, duration_days: 7 });
  const [partnerLinks, setPartnerLinks] = useState({
    solicitor: { name: '', url: '', enabled: false },
    surveyor: { name: '', url: '', enabled: false },
    mortgage: { name: '', url: '', enabled: false },
  });
  const [newBanner, setNewBanner] = useState({ title: '', image_url: '', link_url: '', placement: 'landing', active: true });
  const [showNewBanner, setShowNewBanner] = useState(false);
  const [loadingAds, setLoadingAds] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [bannersRes, adsenseRes, sponsoredRes, linksRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/ads/banners`),
        axios.get(`${API_URL}/api/ads/adsense-config`),
        axios.get(`${API_URL}/api/admin/ads/sponsored-config`),
        axios.get(`${API_URL}/api/admin/partner-links`),
      ]);
      setBanners(bannersRes.data || []);
      if (adsenseRes.data) setAdsenseConfig(adsenseRes.data);
      if (sponsoredRes.data) setSponsoredConfig(sponsoredRes.data);
      if (linksRes.data) setPartnerLinks(linksRes.data);
    } catch (error) {
      toast.error('Failed to load ads config');
    } finally {
      setLoadingAds(false);
    }
  };

  const saveBanner = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/ads/banners`, newBanner);
      toast.success('Banner created');
      setNewBanner({ title: '', image_url: '', link_url: '', placement: 'landing', active: true });
      setShowNewBanner(false);
      fetchAll();
    } catch (error) {
      toast.error('Failed to create banner');
    }
  };

  const toggleBanner = async (id, active) => {
    try {
      await axios.put(`${API_URL}/api/admin/ads/banners/${id}`, { active: !active });
      fetchAll();
    } catch (error) {
      toast.error('Failed to update banner');
    }
  };

  const deleteBanner = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/ads/banners/${id}`);
      toast.success('Banner deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const saveAdsense = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/ads/adsense`, adsenseConfig);
      toast.success('AdSense config saved');
    } catch (error) {
      toast.error('Failed to save AdSense config');
    }
  };

  const saveSponsored = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/ads/sponsored-config`, sponsoredConfig);
      toast.success('Sponsored config saved');
    } catch (error) {
      toast.error('Failed to save sponsored config');
    }
  };

  const savePartnerLinks = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/partner-links`, partnerLinks);
      toast.success('Partner links saved');
    } catch (error) {
      toast.error('Failed to save partner links');
    }
  };

  if (loadingAds) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-8" data-testid="ads-tab">
      {/* Custom Ad Banners */}
      <div className="luxury-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Custom Ad Banners</h3>
            <p className="text-sm text-slate-500 mt-1">Sell ad space to local businesses — you keep 100% revenue</p>
          </div>
          <Button onClick={() => setShowNewBanner(!showNewBanner)} className="bg-[#E07A3A] hover:bg-[#C4652E] text-white text-xs tracking-wider" data-testid="add-banner-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Banner
          </Button>
        </div>

        {showNewBanner && (
          <div className="bg-slate-50 p-5 mb-6 space-y-4 border border-slate-200" data-testid="new-banner-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium uppercase tracking-wider">Title</label>
                <input value={newBanner.title} onChange={e => setNewBanner({...newBanner, title: e.target.value})} placeholder="e.g. ABC Mortgage Brokers" className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid="banner-title-input" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium uppercase tracking-wider">Link URL</label>
                <input value={newBanner.link_url} onChange={e => setNewBanner({...newBanner, link_url: e.target.value})} placeholder="https://partner-website.com" className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid="banner-link-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1 font-medium uppercase tracking-wider">Image URL</label>
              <input value={newBanner.image_url} onChange={e => setNewBanner({...newBanner, image_url: e.target.value})} placeholder="https://example.com/banner.jpg or Cloudinary URL" className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid="banner-image-input" />
              {newBanner.image_url && <img src={newBanner.image_url} alt="Preview" className="mt-2 h-24 object-cover border border-slate-200" />}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewBanner(false)} className="text-xs tracking-wider">Cancel</Button>
              <Button onClick={saveBanner} disabled={!newBanner.title || !newBanner.link_url} className="bg-[#E07A3A] hover:bg-[#C4652E] text-white text-xs tracking-wider" data-testid="save-banner-btn">Save Banner</Button>
            </div>
          </div>
        )}

        {banners.length === 0 ? (
          <div className="text-center py-8">
            <Image className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No ad banners yet. Create one to start earning from partner businesses.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {banners.map((banner) => (
              <div key={banner.id} className="flex items-center gap-4 p-4 border border-slate-200 hover:border-slate-300 transition-colors" data-testid={`banner-row-${banner.id}`}>
                {banner.image_url && <img src={banner.image_url} alt="" className="w-20 h-14 object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#1A1A1A] font-medium truncate">{banner.title || 'Untitled'}</p>
                  <p className="text-xs text-slate-400 truncate">{banner.link_url}</p>
                  <div className="flex gap-3 mt-1 text-xs text-slate-400">
                    <span>{banner.impressions || 0} views</span>
                    <span>{banner.clicks || 0} clicks</span>
                    <span>CTR: {banner.impressions > 0 ? ((banner.clicks / banner.impressions) * 100).toFixed(1) : '0.0'}%</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => toggleBanner(banner.id, banner.active)} className={`px-3 py-1 text-xs tracking-wider font-bold uppercase rounded border ${banner.active ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' : 'bg-slate-900 text-slate-300 border-slate-500/40'}`} data-testid={`banner-toggle-${banner.id}`}>
                    {banner.active ? 'Active' : 'Paused'}
                  </button>
                  <button onClick={() => deleteBanner(banner.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" data-testid={`banner-delete-${banner.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Google AdSense */}
      <div className="luxury-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Google AdSense</h3>
            <p className="text-sm text-slate-500 mt-1">Earn from Google ads on your pages — add your Publisher ID when ready</p>
          </div>
          <button
            onClick={() => setAdsenseConfig({...adsenseConfig, enabled: !adsenseConfig.enabled})}
            className={`px-4 py-2 text-xs tracking-wider font-bold uppercase rounded border ${adsenseConfig.enabled ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' : 'bg-slate-900 text-slate-300 border-slate-500/40'}`}
            data-testid="adsense-toggle"
          >
            {adsenseConfig.enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1 font-medium uppercase tracking-wider">Publisher ID</label>
            <input
              value={adsenseConfig.publisher_id}
              onChange={e => setAdsenseConfig({...adsenseConfig, publisher_id: e.target.value})}
              placeholder="ca-pub-XXXXXXXXXXXXXXXX"
              className="w-full border border-slate-200 px-3 py-2 text-sm font-mono focus:border-[#E07A3A] focus:outline-none"
              data-testid="adsense-publisher-id"
            />
            <p className="text-xs text-slate-400 mt-1">Get this from your Google AdSense account at adsense.google.com</p>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1 font-medium uppercase tracking-wider">Landing Page Ad Slot</label>
            <input
              value={adsenseConfig.slots?.landing || ''}
              onChange={e => setAdsenseConfig({...adsenseConfig, slots: {...adsenseConfig.slots, landing: e.target.value}})}
              placeholder="1234567890"
              className="w-full border border-slate-200 px-3 py-2 text-sm font-mono focus:border-[#E07A3A] focus:outline-none"
              data-testid="adsense-slot-landing"
            />
          </div>
          <div className="flex justify-end">
            <Button onClick={saveAdsense} className="bg-[#E07A3A] hover:bg-[#C4652E] text-white text-xs tracking-wider" data-testid="save-adsense-btn">Save AdSense</Button>
          </div>
        </div>
      </div>

      {/* Sponsored Listings */}
      <div className="luxury-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Sponsored Listings</h3>
            <p className="text-sm text-slate-500 mt-1">Sellers pay to feature their property at the top of search results</p>
          </div>
          <button
            onClick={() => setSponsoredConfig({...sponsoredConfig, enabled: !sponsoredConfig.enabled})}
            className={`px-4 py-2 text-xs tracking-wider font-bold uppercase rounded border ${sponsoredConfig.enabled ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' : 'bg-slate-900 text-slate-300 border-slate-500/40'}`}
            data-testid="sponsored-toggle"
          >
            {sponsoredConfig.enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1 font-medium">UK Fee (£)</label>
            <input type="number" value={sponsoredConfig.fees?.UK || 0} onChange={e => setSponsoredConfig({...sponsoredConfig, fees: {...sponsoredConfig.fees, UK: parseInt(e.target.value)}})}
              className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid="sponsored-fee-uk" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1 font-medium">India Fee (₹)</label>
            <input type="number" value={sponsoredConfig.fees?.IN || 0} onChange={e => setSponsoredConfig({...sponsoredConfig, fees: {...sponsoredConfig.fees, IN: parseInt(e.target.value)}})}
              className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid="sponsored-fee-in" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1 font-medium">Canada Fee (C$)</label>
            <input type="number" value={sponsoredConfig.fees?.CA || 0} onChange={e => setSponsoredConfig({...sponsoredConfig, fees: {...sponsoredConfig.fees, CA: parseInt(e.target.value)}})}
              className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid="sponsored-fee-ca" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1 font-medium">Duration (days)</label>
            <input type="number" value={sponsoredConfig.duration_days || 7} onChange={e => setSponsoredConfig({...sponsoredConfig, duration_days: parseInt(e.target.value)})}
              className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-[#E07A3A] focus:outline-none" data-testid="sponsored-duration" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={saveSponsored} className="bg-[#E07A3A] hover:bg-[#C4652E] text-white text-xs tracking-wider" data-testid="save-sponsored-btn">Save Sponsored Config</Button>
        </div>
      </div>

      {/* Partner Links */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold mb-2">Partner Referral Links</h3>
        <p className="text-sm text-slate-500 mb-6">Set external partner URLs for the referral buttons. Leave empty if you don't have partners yet.</p>
        <div className="space-y-4">
          {[
            { key: 'solicitor', label: 'Solicitor / Conveyancer' },
            { key: 'surveyor', label: 'Property Surveyor' },
            { key: 'mortgage', label: 'Mortgage Adviser' },
          ].map((partner) => (
            <div key={partner.key} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border border-slate-100 p-4" data-testid={`partner-${partner.key}`}>
              <div className="md:col-span-3">
                <label className="block text-xs text-slate-500 mb-1 font-medium">{partner.label} Name</label>
                <input
                  value={partnerLinks[partner.key]?.name || ''}
                  onChange={e => setPartnerLinks({...partnerLinks, [partner.key]: {...partnerLinks[partner.key], name: e.target.value}})}
                  placeholder="Partner company name"
                  className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-[#E07A3A] focus:outline-none"
                />
              </div>
              <div className="md:col-span-6">
                <label className="block text-xs text-slate-500 mb-1 font-medium">URL</label>
                <input
                  value={partnerLinks[partner.key]?.url || ''}
                  onChange={e => setPartnerLinks({...partnerLinks, [partner.key]: {...partnerLinks[partner.key], url: e.target.value}})}
                  placeholder="https://partner-website.com"
                  className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-[#E07A3A] focus:outline-none"
                />
              </div>
              <div className="md:col-span-3 flex items-center gap-2">
                <button
                  onClick={() => setPartnerLinks({...partnerLinks, [partner.key]: {...partnerLinks[partner.key], enabled: !partnerLinks[partner.key]?.enabled}})}
                  className={`px-3 py-2 text-xs tracking-wider font-bold uppercase rounded border flex-1 ${partnerLinks[partner.key]?.enabled ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' : 'bg-slate-900 text-slate-300 border-slate-500/40'}`}
                >
                  {partnerLinks[partner.key]?.enabled ? 'Active' : 'Inactive'}
                </button>
                {partnerLinks[partner.key]?.url && (
                  <a href={partnerLinks[partner.key].url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-[#E07A3A]">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={savePartnerLinks} className="bg-[#E07A3A] hover:bg-[#C4652E] text-white text-xs tracking-wider" data-testid="save-partner-links-btn">Save Partner Links</Button>
        </div>
      </div>
    </div>
  );
};
